import type { ProceduralTimelineEvent } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';

/**
 * Outlook 365 deep-link builder: opens a pre-filled compose view in the user's
 * own Outlook, so the organiser is whoever exports it. Outlook generates its
 * own event id on save, so re-clicking creates a new event each time.
 */

const DEFAULT_DURATION_MS = 60 * 60 * 1000;
const OUTLOOK_COMPOSE_URL = 'https://outlook.office.com/calendar/0/deeplink/compose';
/**
 * Outlook's web compose endpoint silently truncates the body and attendee list
 * past ~2k characters. Microsoft doesn't document the limit; this is empirical.
 */
export const OUTLOOK_DEEP_LINK_MAX_URL_LENGTH = 1900;

interface DeepLinkInputs {
  event: ProceduralTimelineEvent;
  attendeeEmails: string[];
  wexlerUrl: string;
  creatorEmail: string;
}

export function buildOutlookCalendarUrl({ event, attendeeEmails, wexlerUrl, creatorEmail }: DeepLinkInputs): string {
  if (event.startDateTime === null) {
    throw new Error('Cannot build calendar link for an event with no start date');
  }
  const start = new Date(event.startDateTime);
  const isAllDay = !hasTimeComponent(start);

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.name,
    body: buildBody(wexlerUrl, creatorEmail),
    allday: isAllDay ? 'true' : 'false',
  });

  if (isAllDay) {
    // Date-only range. Outlook treats the all-day end as EXCLUSIVE (the event's
    // last day is enddt - 1), so add a day to land on the intended final day.
    const lastDay = event.endDateTime !== null ? new Date(event.endDateTime) : start;
    params.set('startdt', formatDateOnly(start));
    params.set('enddt', formatDateOnly(addUtcDay(lastDay)));
  } else {
    // Outlook's deep-link has no timezone param, so emit a fully-qualified ISO
    // stamp with the event timezone's actual offset. The event then lands at
    // the right instant regardless of the viewing user's calendar tz.
    const end =
      event.endDateTime !== null ? new Date(event.endDateTime) : new Date(start.getTime() + DEFAULT_DURATION_MS);
    params.set('startdt', formatIsoWithOffset(start, event.timeZone));
    params.set('enddt', formatIsoWithOffset(end, event.timeZone));
  }

  if (attendeeEmails.length > 0) {
    params.set('to', attendeeEmails.join(','));
  }
  return `${OUTLOOK_COMPOSE_URL}?${params.toString()}`;
}

function buildBody(wexlerUrl: string, creatorEmail: string): string {
  const wexlerLink = `<a href="${escapeHtml(wexlerUrl)}">View the event in Wexler</a>`;
  return [`This event was created by ${escapeHtml(creatorEmail)}.`, wexlerLink].join('<br>');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function hasTimeComponent(date: Date): boolean {
  return date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0 || date.getUTCSeconds() !== 0;
}

function formatDateOnly(date: Date): string {
  return (
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}` +
    `-${String(date.getUTCDate()).padStart(2, '0')}`
  );
}

// Next calendar day in UTC; Date.UTC handles month/year rollover.
function addUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1));
}

function formatIsoWithOffset(date: Date, tzId: string | null): string {
  const naive =
    `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}` +
    `T${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`;
  if (tzId === null || tzId.trim() === '') {
    return `${naive}Z`;
  }
  return `${naive}${computeOffset(date, tzId)}`;
}

/** `+HH:MM` / `-HH:MM` for the wall-clock instant in `tzId`. Uses `Intl.DateTimeFormat` to handle DST. */
function computeOffset(date: Date, tzId: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tzId, timeZoneName: 'longOffset' });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    const match = /GMT([+-])(\d{1,2})(?::?(\d{2}))?/.exec(tzPart);
    if (match === null) return 'Z';
    const sign = match[1];
    const hours = String(parseInt(match[2], 10)).padStart(2, '0');
    const minutes = match[3] !== undefined ? match[3] : '00';
    return `${sign}${hours}:${minutes}`;
  } catch {
    return 'Z';
  }
}
